import { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import type { DiscountOptions, MenuCategoryOption, MenuGroupOption, PosEntityOption } from '@/types/api';

interface ManageItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  options: DiscountOptions;
  selectedItemIds: number[];
  onChange: (ids: number[]) => void;
}

interface TreeNode {
  type: 'category' | 'group' | 'item';
  posId: number;
  name: string;
  children?: TreeNode[];
  itemPosIds?: number[]; // for category/group: all item POS IDs underneath
}

export function ManageItemsModal({ isOpen, onClose, options, selectedItemIds, onChange }: ManageItemsModalProps) {
  const [search, setSearch] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const searchRef = useRef<HTMLInputElement>(null);

  // Focus search when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 100);
    } else {
      setSearch('');
    }
  }, [isOpen]);

  // Build tree: Category → Group → Item
  const tree = useMemo(() => {
    // Build a map: groupPosId → items
    const groupItemsMap = new Map<number, PosEntityOption[]>();
    for (const item of options.menuItems) {
      // Find which group this item belongs to by checking group.menuItemPosIds
      for (const group of options.menuGroups) {
        if ((group as MenuGroupOption).menuItemPosIds.includes(item.posId)) {
          if (!groupItemsMap.has(group.posId)) groupItemsMap.set(group.posId, []);
          groupItemsMap.get(group.posId)!.push(item);
          break;
        }
      }
    }

    // Build a map: categoryPosId → groups
    const catGroupsMap = new Map<number, MenuGroupOption[]>();
    for (const group of options.menuGroups) {
      // Find which category this group belongs to by checking category.menuItemPosIds overlap
      for (const cat of options.menuCategories) {
        const catItemIds = new Set((cat as MenuCategoryOption).menuItemPosIds);
        const groupItemIds = (group as MenuGroupOption).menuItemPosIds;
        if (groupItemIds.length > 0 && groupItemIds.some(id => catItemIds.has(id))) {
          if (!catGroupsMap.has(cat.posId)) catGroupsMap.set(cat.posId, []);
          catGroupsMap.get(cat.posId)!.push(group as MenuGroupOption);
          break;
        }
      }
    }

    // Items not in any group
    const itemsInGroups = new Set<number>();
    for (const group of options.menuGroups) {
      for (const id of (group as MenuGroupOption).menuItemPosIds) {
        itemsInGroups.add(id);
      }
    }
    const orphanItems = options.menuItems.filter(i => !itemsInGroups.has(i.posId));

    const nodes: TreeNode[] = [];

    for (const cat of options.menuCategories) {
      const catOption = cat as MenuCategoryOption;
      const groups = catGroupsMap.get(cat.posId) || [];
      if (groups.length === 0 && catOption.menuItemPosIds.length === 0) continue;

      const groupNodes: TreeNode[] = groups.map(group => {
        const items = groupItemsMap.get(group.posId) || [];
        return {
          type: 'group' as const,
          posId: group.posId,
          name: group.name.includes(' > ') ? group.name.split(' > ').pop()! : group.name,
          itemPosIds: (group as MenuGroupOption).menuItemPosIds,
          children: items.map(item => ({
            type: 'item' as const,
            posId: item.posId,
            name: item.name.includes(' > ') ? item.name.split(' > ').pop()! : item.name,
          })),
        };
      });

      nodes.push({
        type: 'category',
        posId: cat.posId,
        name: cat.name,
        itemPosIds: catOption.menuItemPosIds,
        children: groupNodes,
      });
    }

    // Add orphan items under "Other"
    if (orphanItems.length > 0) {
      nodes.push({
        type: 'category',
        posId: -1,
        name: 'Other',
        itemPosIds: orphanItems.map(i => i.posId),
        children: orphanItems.map(item => ({
          type: 'item' as const,
          posId: item.posId,
          name: item.name.includes(' > ') ? item.name.split(' > ').pop()! : item.name,
        })),
      });
    }

    return nodes;
  }, [options]);

  // Filter tree by search
  const filteredTree = useMemo(() => {
    if (!search.trim()) return tree;
    const q = search.toLowerCase();

    return tree
      .map(cat => {
        const catMatch = cat.name.toLowerCase().includes(q);
        const filteredChildren = (cat.children || [])
          .map(group => {
            const groupMatch = group.name.toLowerCase().includes(q);
            const filteredItems = (group.children || []).filter(item =>
              item.name.toLowerCase().includes(q)
            );
            if (groupMatch || filteredItems.length > 0) {
              return { ...group, children: groupMatch ? group.children : filteredItems };
            }
            return null;
          })
          .filter(Boolean) as TreeNode[];

        if (catMatch || filteredChildren.length > 0) {
          return { ...cat, children: catMatch ? cat.children : filteredChildren };
        }
        return null;
      })
      .filter(Boolean) as TreeNode[];
  }, [tree, search]);

  // Auto-expand all when searching
  const effectiveExpanded = useMemo(() => {
    if (search.trim()) {
      const all = new Set<string>();
      for (const cat of filteredTree) {
        all.add(`cat-${cat.posId}`);
        for (const group of cat.children || []) {
          all.add(`grp-${group.posId}`);
        }
      }
      return all;
    }
    return expandedNodes;
  }, [search, filteredTree, expandedNodes]);

  const toggleExpand = (key: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedSet = useMemo(() => new Set(selectedItemIds), [selectedItemIds]);

  const getCheckState = (node: TreeNode): 'checked' | 'unchecked' | 'indeterminate' => {
    if (node.type === 'item') {
      return selectedSet.has(node.posId) ? 'checked' : 'unchecked';
    }
    const itemIds = node.itemPosIds || [];
    if (itemIds.length === 0) return 'unchecked';
    const selectedCount = itemIds.filter(id => selectedSet.has(id)).length;
    if (selectedCount === 0) return 'unchecked';
    if (selectedCount === itemIds.length) return 'checked';
    return 'indeterminate';
  };

  const toggleNode = (node: TreeNode) => {
    if (node.type === 'item') {
      if (selectedSet.has(node.posId)) {
        onChange(selectedItemIds.filter(id => id !== node.posId));
      } else {
        onChange([...selectedItemIds, node.posId]);
      }
    } else {
      const itemIds = node.itemPosIds || [];
      const state = getCheckState(node);
      if (state === 'checked') {
        // Uncheck all items under this node
        const toRemove = new Set(itemIds);
        onChange(selectedItemIds.filter(id => !toRemove.has(id)));
      } else {
        // Check all items under this node
        const next = new Set(selectedItemIds);
        itemIds.forEach(id => next.add(id));
        onChange([...next]);
      }
    }
  };

  const selectedCount = selectedItemIds.length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalHeader onClose={onClose}>
        Manage Menu Items ({selectedCount} selected)
      </ModalHeader>
      <ModalBody>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            ref={searchRef}
            type="text"
            placeholder="Search items, groups, or categories..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input w-full pl-9"
          />
        </div>
        <div className="border rounded max-h-[50vh] overflow-y-auto">
          {filteredTree.length === 0 ? (
            <div className="p-4 text-sm text-gray-400 text-center">No items found</div>
          ) : (
            filteredTree.map(cat => (
              <CategoryNode
                key={`cat-${cat.posId}`}
                node={cat}
                expanded={effectiveExpanded}
                onToggleExpand={toggleExpand}
                onToggleCheck={toggleNode}
                getCheckState={getCheckState}
              />
            ))
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <button type="button" onClick={onClose} className="btn btn-primary">
          Done
        </button>
      </ModalFooter>
    </Modal>
  );
}

// --- Tree node components ---

interface NodeProps {
  node: TreeNode;
  expanded: Set<string>;
  onToggleExpand: (key: string) => void;
  onToggleCheck: (node: TreeNode) => void;
  getCheckState: (node: TreeNode) => 'checked' | 'unchecked' | 'indeterminate';
}

function TriCheckbox({ state, onChange }: { state: 'checked' | 'unchecked' | 'indeterminate'; onChange: () => void }) {
  return (
    <input
      type="checkbox"
      checked={state === 'checked'}
      ref={(el) => { if (el) el.indeterminate = state === 'indeterminate'; }}
      onChange={onChange}
      className="rounded border-gray-300 text-blue-600"
    />
  );
}

function CategoryNode({ node, expanded, onToggleExpand, onToggleCheck, getCheckState }: NodeProps) {
  const key = `cat-${node.posId}`;
  const isExpanded = expanded.has(key);
  const state = getCheckState(node);

  return (
    <div>
      <div className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 border-b border-gray-100">
        <button
          type="button"
          onClick={() => onToggleExpand(key)}
          className="p-0.5 text-gray-400 hover:text-gray-600"
        >
          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <TriCheckbox state={state} onChange={() => onToggleCheck(node)} />
        <span className="text-sm font-medium text-gray-800">{node.name}</span>
        <span className="text-xs text-gray-400 ml-auto">{node.itemPosIds?.length || 0} items</span>
      </div>
      {isExpanded && node.children?.map(group => (
        group.type === 'group' ? (
          <GroupNode
            key={`grp-${group.posId}`}
            node={group}
            expanded={expanded}
            onToggleExpand={onToggleExpand}
            onToggleCheck={onToggleCheck}
            getCheckState={getCheckState}
          />
        ) : (
          <ItemNode
            key={`item-${group.posId}`}
            node={group}
            onToggleCheck={onToggleCheck}
            getCheckState={getCheckState}
            depth={1}
          />
        )
      ))}
    </div>
  );
}

function GroupNode({ node, expanded, onToggleExpand, onToggleCheck, getCheckState }: NodeProps) {
  const key = `grp-${node.posId}`;
  const isExpanded = expanded.has(key);
  const state = getCheckState(node);

  return (
    <div>
      <div className="flex items-center gap-2 pl-8 pr-3 py-1.5 hover:bg-gray-50 border-b border-gray-50">
        <button
          type="button"
          onClick={() => onToggleExpand(key)}
          className="p-0.5 text-gray-400 hover:text-gray-600"
        >
          {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
        <TriCheckbox state={state} onChange={() => onToggleCheck(node)} />
        <span className="text-sm text-gray-700">{node.name}</span>
        <span className="text-xs text-gray-400 ml-auto">{node.itemPosIds?.length || 0}</span>
      </div>
      {isExpanded && node.children?.map(item => (
        <ItemNode
          key={`item-${item.posId}`}
          node={item}
          onToggleCheck={onToggleCheck}
          getCheckState={getCheckState}
          depth={2}
        />
      ))}
    </div>
  );
}

function ItemNode({ node, onToggleCheck, getCheckState, depth }: {
  node: TreeNode;
  onToggleCheck: (node: TreeNode) => void;
  getCheckState: (node: TreeNode) => 'checked' | 'unchecked' | 'indeterminate';
  depth: number;
}) {
  const state = getCheckState(node);
  const pl = depth === 1 ? 'pl-12' : 'pl-16';

  return (
    <label className={`flex items-center gap-2 ${pl} pr-3 py-1 hover:bg-blue-50 cursor-pointer border-b border-gray-50`}>
      <input
        type="checkbox"
        checked={state === 'checked'}
        onChange={() => onToggleCheck(node)}
        className="rounded border-gray-300 text-blue-600"
      />
      <span className="text-sm text-gray-600">{node.name}</span>
    </label>
  );
}
